import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(e.response?.data?.detail || "Falha ao salvar");

const MoveDialog = ({ pending, stages, onClose, onDone }) => {
  const others = stages.filter((x) => x.id !== pending?.stage.id);
  const [target, setTarget] = useState("");
  const confirm = async () => {
    if (!target) return toast.error("Escolha a coluna de destino");
    await api.delete(`/pipeline/stages/${pending.stage.id}`, { params: { move_to: target } }).then(onDone).catch(err);
    onClose();
  };
  return (
    <Dialog open={!!pending} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="stage-move-dialog">
        <DialogHeader><DialogTitle>Excluir coluna “{pending?.stage.name}”</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{pending?.message}</p>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full h-10 border border-border rounded-md bg-background px-2 text-sm" data-testid="stage-move-select">
          <option value="">Mover oportunidades para…</option>
          {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <DialogFooter><Button onClick={confirm} variant="destructive" data-testid="stage-move-confirm">Mover e excluir</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StageRow = ({ s, i, total, stages, onChanged, onNeedMove }) => {
  const [name, setName] = useState(s.name);
  const save = (patch) => api.patch(`/pipeline/stages/${s.id}`, patch).then(onChanged).catch(err);
  const move = async (dir) => {
    const ids = stages.map((x) => x.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    await api.put("/pipeline/stages/reorder", { ids });
    onChanged();
  };
  const remove = async () => {
    if (!window.confirm(`Excluir a coluna "${s.name}"?`)) return;
    try {
      await api.delete(`/pipeline/stages/${s.id}`);
      onChanged();
    } catch (e) {
      if (e.response?.status !== 409) return err(e);
      onNeedMove({ stage: s, message: e.response.data.detail });
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="stage-row">
      <input type="color" value={s.color} onChange={(e) => save({ color: e.target.value })} className="w-9 h-9 rounded cursor-pointer border border-border bg-transparent" data-testid="stage-color" />
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== s.name && save({ name: name.trim() })} className="h-9" data-testid="stage-name" />
      <select value={s.kind} onChange={(e) => save({ kind: e.target.value })} className="h-9 border border-border rounded-md bg-background px-2 text-xs" data-testid="stage-kind">
        <option value="open">Em andamento</option><option value="won">Ganho</option><option value="lost">Perdido</option>
      </select>
      <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(-1)} data-testid="stage-up"><ArrowUp className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" disabled={i === total - 1} onClick={() => move(1)} data-testid="stage-down"><ArrowDown className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" className="text-destructive" onClick={remove} data-testid="stage-delete"><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};

const StatusRow = ({ s, onChanged }) => {
  const [name, setName] = useState(s.name);
  const save = (patch) => api.patch(`/opp-statuses/${s.id}`, { name: s.name, color: s.color, ...patch }).then(onChanged).catch(err);
  return (
    <div className="flex items-center gap-2" data-testid="status-row">
      <input type="color" value={s.color} onChange={(e) => save({ color: e.target.value })} className="w-9 h-9 rounded-full cursor-pointer border border-border bg-transparent" data-testid="status-color" />
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== s.name && save({ name: name.trim() })} className="h-9" data-testid="status-name" />
      <Button size="icon" variant="ghost" className="text-destructive" data-testid="status-delete"
              onClick={() => window.confirm(`Excluir o status "${s.name}"?`) && api.delete(`/opp-statuses/${s.id}`).then(onChanged)}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const PipelineManager = ({ open, onOpenChange, stages, statuses, onChanged }) => {
  const [pending, setPending] = useState(null);
  const addStage = () => api.post("/pipeline/stages", { name: "Nova coluna", color: "#64748b", kind: "open" }).then(onChanged).catch(err);
  const addStatus = () => api.post("/opp-statuses", { name: "Novo status", color: "#10b981" }).then(onChanged).catch(err);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pipeline-manager">
        <DialogHeader><DialogTitle>Personalizar funil</DialogTitle></DialogHeader>
        <Tabs defaultValue="stages">
          <TabsList><TabsTrigger value="stages" data-testid="pm-tab-stages">Colunas</TabsTrigger><TabsTrigger value="statuses" data-testid="pm-tab-statuses">Status (bolinhas)</TabsTrigger></TabsList>
          <TabsContent value="stages" className="space-y-2 mt-3">
            {stages.map((s, i) => <StageRow key={`${s.id}-${s.name}-${s.color}`} s={s} i={i} total={stages.length} stages={stages} onChanged={onChanged} onNeedMove={setPending} />)}
            <Button size="sm" variant="outline" onClick={addStage} data-testid="stage-add"><Plus className="w-4 h-4 mr-1" /> Adicionar coluna</Button>
          </TabsContent>
          <TabsContent value="statuses" className="space-y-2 mt-3">
            {statuses.map((s) => <StatusRow key={`${s.id}-${s.name}-${s.color}`} s={s} onChanged={onChanged} />)}
            <Button size="sm" variant="outline" onClick={addStatus} data-testid="status-add"><Plus className="w-4 h-4 mr-1" /> Adicionar status</Button>
          </TabsContent>
        </Tabs>
        <MoveDialog key={pending?.stage.id || "none"} pending={pending} stages={stages} onClose={() => setPending(null)} onDone={onChanged} />
      </DialogContent>
    </Dialog>
  );
};
